import React, { useState, useEffect, useRef, memo } from "react";
import { format } from "date-fns";
import { CalendarIcon, Clock } from "lucide-react";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

// 1. MOVE TimeColumn OUTSIDE to prevent unmounting/remounting on every parent render
const TimeColumn = memo(({ items, current, type, onValueChange }) => {
  const scrollRef = useRef(null);
  const lastValue = useRef(current);

  // Handle scrolling only when the "current" value for THIS column actually changes
  useEffect(() => {
    if (scrollRef.current && current !== lastValue.current) {
      const selectedElement = scrollRef.current.querySelector(`[data-value="${current}"]`);
      if (selectedElement) {
        scrollRef.current.scrollTo({
          top: selectedElement.offsetTop,
          behavior: "smooth",
        });
      }
      lastValue.current = current;
    }
  }, [current]);

  // Initial position without animation when component first mounts
  useEffect(() => {
    if (scrollRef.current) {
      const selectedElement = scrollRef.current.querySelector(`[data-value="${current}"]`);
      if (selectedElement) {
        scrollRef.current.scrollTop = selectedElement.offsetTop;
      }
    }
  }, []);

  return (
    <div
      ref={scrollRef}
      className="h-full overflow-y-auto no-scrollbar snap-y snap-mandatory scroll-smooth w-full relative"
    >
      {items.map((v) => (
        <div
          key={v}
          data-value={v}
          className="snap-start h-10 flex items-center justify-center"
        >
          <button
            onClick={() => onValueChange(v, type)}
            className={cn(
              "text-base transition-all duration-200 w-full h-full flex items-center justify-center",
              current === v ? "text-[#C49B3A] font-bold" : "text-gray-400/60 hover:text-gray-600"
            )}
          >
            {v}
          </button>
        </div>
      ))}
      {/* Spacer to allow bottom items to scroll to the top selection bar */}
      <div className="h-[175px]" />
    </div>
  );
});

TimeColumn.displayName = "TimeColumn";

const DateTimePicker = ({ label, onChange, value, error }) => {
  const [date, setDate] = useState(value || null);
  const [hour, setHour] = useState(value ? format(value, "hh") : "09");
  const [minute, setMinute] = useState(value ? format(value, "mm") : "00");
  const [ampm, setAmpm] = useState(value ? format(value, "a").toUpperCase() : "AM");

  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, "0"));
  const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, "0"));
  const ampmOptions = ["AM", "PM"];

  // Sync state with incoming value prop, but only if they are actually different
  useEffect(() => {
    if (value) {
      const newHour = format(value, "hh");
      const newMinute = format(value, "mm");
      const newAmpm = format(value, "a").toUpperCase();

      if (value.getTime() !== date?.getTime()) setDate(value);
      if (newHour !== hour) setHour(newHour);
      if (newMinute !== minute) setMinute(newMinute);
      if (newAmpm !== ampm) setAmpm(newAmpm);
    }
  }, [value]);

  const triggerChange = (selectedDate, h, m, a) => {
    if (!selectedDate || !onChange) return;
    let hoursInt = parseInt(h);
    if (a === "PM" && hoursInt !== 12) hoursInt += 12;
    if (a === "AM" && hoursInt === 12) hoursInt = 0;

    const finalDateTime = new Date(selectedDate);
    finalDateTime.setHours(hoursInt, parseInt(m), 0, 0);
    const timeString = `${h}:${m} ${a}`;
    onChange(finalDateTime, timeString);
  };

  const handleValueChange = (newVal, type) => {
    let h = hour, m = minute, a = ampm;
    if (type === "h") { h = newVal; setHour(newVal); }
    else if (type === "m") { m = newVal; setMinute(newVal); }
    else if (type === "a") { a = newVal; setAmpm(newVal); }

    triggerChange(date, h, m, a);
  };

  return (
    <div className="w-full">
      <style dangerouslySetInnerHTML={{ __html: `
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />

      {label && <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>}

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-between text-left border bg-white px-4 py-6 rounded-xl shadow-sm hover:border-[#C49B3A]/50 transition-all",
              error ? "border-red-500" : "border-gray-300"
            )}
          >
            <div className="flex items-center gap-3">
              <CalendarIcon size={18} className="text-[#C49B3A]" />
              <span className="text-gray-700 font-medium">
                {value ? `${format(value, "PPP")} • ${format(value, "hh:mm a")}` : "Select date & time"}
              </span>
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="bg-white shadow-2xl p-0 w-auto flex flex-col md:flex-row border border-gray-100 rounded-[24px] overflow-hidden"
          align="start"
        >
          {/* Calendar Section */}
          <div className="p-4 border-r border-gray-50 bg-white">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => {
                if (d) {
                  setDate(d);
                  triggerChange(d, hour, minute, ampm);
                }
              }}
              disabled={(d) => d < new Date().setHours(0, 0, 0, 0)}
            />
          </div>

          {/* Time Picker Section */}
          <div className="flex flex-col w-[240px] bg-white relative">
            <div className="p-4 border-b border-gray-50 flex items-center justify-center gap-2">
              <Clock size={16} className="text-[#C49B3A]" />
              <span className="font-bold text-sm text-gray-800">Set Time</span>
            </div>

            <div className="relative flex flex-row items-start justify-center h-[215px] px-4 pt-2 overflow-hidden">
              <div className="absolute top-2 left-4 right-4 h-10 bg-gray-100/80 rounded-lg pointer-events-none z-0" />

              <div className="relative z-10 flex flex-row items-start w-full h-full">
                <TimeColumn items={hours} current={hour} type="h" onValueChange={handleValueChange} />
                <span className="text-gray-400 font-bold h-10 flex items-center mx-1">:</span>
                <TimeColumn items={minutes} current={minute} type="m" onValueChange={handleValueChange} />
                <div className="w-2" />
                <TimeColumn items={ampmOptions} current={ampm} type="a" onValueChange={handleValueChange} />
              </div>
            </div>

            <div className="px-4 py-4 bg-gray-50/50 mt-auto border-t border-gray-50 flex flex-col items-center">
              <div className="font-bold text-sm text-gray-800 tracking-tight">
                {hour}:{minute} {ampm}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DateTimePicker;
